import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notify } from '../../helpers/toastify';

import BasicTable from '../molecules/BasicTable';
import {
  Button,
  InformationTip,
  SingleLineText,
  TitlePart,
  HrefLink,
  DatePicker,
  DatePickerValue,
  Modal,
} from '@bloomays-lib/ui.shared';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { ActionBundle } from '../molecules/ActionBundle';
import InvoicesUpload from '../molecules/InvoicesUpload';
import Typography from '@mui/material/Typography';
import { IActivity, IMission, Role } from '@bloomays-lib/types.shared';
import { ApolloError, ApolloQueryResult, FetchResult } from '@apollo/client';
import { styled } from '@mui/material/styles';
import { convertDateToStringFormat, fixed, isZeroCRAActivity, lastDateOfMonth } from '@bloomays-lib/utils.shared';
import { getApolloErrorCode } from '../../helpers/error';
import { checkIsFinished } from '../../helpers/CRA';
import { UploadFileActivity, ActivitySummary, FileActivityObject } from '../../types/components';
import Stack from '@mui/material/Stack';
import { useAuthStore } from '../../stores';
import { isAfter } from 'date-fns/fp/isAfter';
import { isBefore } from 'date-fns/fp/isBefore';
import { startOfMonth } from 'date-fns/fp/startOfMonth';
import { endOfMonth } from 'date-fns/fp/endOfMonth';

export type TableManagementCRAProps = {
  path?: string[];
  error?: ApolloError;
  displayNewActivity: boolean;
  displayRow: () => void;
  newActivityValue: DatePickerValue;
  arrayOfCRA: ActivitySummary[];
  cancelBloomerActivity: (idDocument: string, row: number | null) => Promise<void>;
  uploadCRA: UploadFileActivity;
  uploadInvoice: UploadFileActivity;
  setNewActivityValue: React.Dispatch<React.SetStateAction<DatePickerValue>>;
  mission: IMission;
  signBloomerActivity: (activityId: string, signerFullname: string) => Promise<void>;
  refetchActivities: (
    variables?:
      | Partial<{
          recordId: string;
        }>
      | undefined,
  ) => Promise<ApolloQueryResult<any>>;
  upsertFollowUp: (args: {
    selected?: number;
    comment?: string;
    collectDate: Date;
    activityId: string;
  }) => Promise<FetchResult<any> | undefined>;
  stateNPS: { loading: boolean };
  getNPS: any;
  deleteInvoice: (activityRecordID: string) => Promise<void>;
};

const TableManagementCRA = ({
  stateNPS,
  error,
  displayNewActivity,
  displayRow,
  newActivityValue,
  arrayOfCRA,
  cancelBloomerActivity,
  uploadCRA,
  uploadInvoice,
  setNewActivityValue,
  mission,
  signBloomerActivity,
  refetchActivities,
  upsertFollowUp,
  getNPS,
  deleteInvoice,
}: TableManagementCRAProps) => {
  const auth = useAuthStore((state) => state.auth);
  const navigate = useNavigate();
  const param = useParams<{ missionRecordId: string }>();
  const [open, setOpen] = useState(false);
  const [modalUploadFile, setModalUploadFile] = useState<{
    show: boolean;
    id?: string | null;
    urlInvoiceS3?: string;
    urlAdditionalInvoicesS3?: string;
  }>({
    show: false,
    id: null,
  });
  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (modalUploadFile?.show === true) {
      handleOpen();
    }
    if (modalUploadFile?.show === false) {
      handleClose();
    }
  }, [modalUploadFile]);

  if (error) {
    let textError;
    const errorCode = getApolloErrorCode(error);
    switch (errorCode) {
      case 'UNKNOWN_RECORD_ID':
        textError = "Cette mission n'existe pas";
        break;
      case 'OBJECT_NOT_OWNED':
        textError = "L'id de cette mission ne semble pas vous appartenir";
        break;
      default:
        textError = 'une erreur non identifiée est survenue';
    }
    notify('error', 'Impossible de récupérer tes données. Erreur du server, essaie plus tard ! 😓 ', textError);
  }

  const createRow = (dateSelected: Date) => {
    const arrayOfCRACopy = [...arrayOfCRA];
    let valueToObjectDate;
    if (newActivityValue) {
      valueToObjectDate = new Date(newActivityValue);
    } else {
      valueToObjectDate = new Date();
    }
    const exist = checkExistingCRA(arrayOfCRACopy, valueToObjectDate);
    if (exist?.length > 0) return;

    const finished = checkIsFinished(mission as IMission, valueToObjectDate);
    if (finished) return;
    const date = convertDateToStringFormat(lastDateOfMonth(dateSelected), 'yyyy-MM-dd');
    navigate(`/missions/${param.missionRecordId}/cra/${date}`);
  };

  const checkExistingCRA = (arrayCRA: ActivitySummary[], date: Date) => {
    const selectedMonth = date.getMonth();
    const selectedYear = date.getFullYear();
    const isExist = arrayCRA.filter((cra) => {
      return cra.monthNumber === selectedMonth && cra.date.getFullYear() === selectedYear;
    });
    if (isExist.length > 0) {
      notify('info', 'Le CRA pour ce mois-ci existe déjà !');
    }
    return isExist;
  };

  const editionPage = (dateSelected: Date) => {
    const date = convertDateToStringFormat(lastDateOfMonth(dateSelected), 'yyyy-MM-dd');
    navigate(`/missions/${param.missionRecordId}/cra/${date}`);
  };

  const isAdmin = auth?.user?.roles?.includes(Role.Admin);
  const bloomerUsePortage = mission.bloomerBillingSociety?.[0]?.portage === true;

  const displayStatut = (el: IActivity) => {
    if (el.recordId) {
      const lastSupplierError = el.lastSupplierError;
      const lastSupplierErrorTooltip = lastSupplierError ? <InformationTip>{lastSupplierError}</InformationTip> : null;
      if (el.supplierInvoiceStatus === 'refused') {
        return (
          <>
            ❌ Facture {bloomerUsePortage ? 'de portage' : 'Bloomer'} refusée {lastSupplierErrorTooltip}
          </>
        );
      }
      if (!el?.urlInvoicesS3) {
        switch (el.CRAStatus) {
          case 'Non crée':
            return 'CRA non créé';
          case 'Validé client':
            return `Validé Client, en attente de la facture ${bloomerUsePortage ? 'de portage' : 'Bloomer'}`;
          case 'Forcé Bloomays':
            return `Validé Bloomays, en attente de la facture ${bloomerUsePortage ? 'de portage' : 'Bloomer'}`;
          case 'Attente signature Client':
            return 'En cours de signature du client';
          case 'Attente signature Bloomer':
            return 'En attente de signature Bloomer';
          case 'Signature non créee':
            return 'CRA à valider par signature Bloomer';
          default:
            return el.CRAStatus;
        }
      } else {
        if (!el.payed && el.lastSupplierError) {
          return (
            <>
              🕦 Facture {bloomerUsePortage ? 'de portage' : 'Bloomer'} en cours de vérification{' '}
              {lastSupplierErrorTooltip}
            </>
          );
        }
        if (el.payed) {
          return `Facture ${bloomerUsePortage ? 'de portage' : 'Bloomer'} mise au réglément à écheance`;
        }
        return `Facture ${bloomerUsePortage ? 'de portage' : 'Bloomer'} receptionnée`;
      }
    } else {
      return "En cours d'édition";
    }
  };

  const getPlural = (nb: number) => {
    const sentence = `${fixed(nb, 4)} jour`;
    if (nb > 1) {
      return sentence + 's';
    }
    return sentence;
  };

  const getExplanation = (el: IActivity) => {
    if (isZeroCRAActivity(el)) {
      return "Aucun jour travaillé déclaré (n'ayant pas travaillé ce mois-ci)";
    }

    return `${getPlural(el.nbDaysWorked)} + ${getPlural(el.nbDaysMarkupWorked)} samedi + ${getPlural(
      fixed(el.nbDaysMarkup2Worked + (el.astreinte || 0)),
    )} dimanche / férié / astreinte`;
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        additionnalCSS={{
          borderRadius: '20px',
          width: '622px',
          padding: '32px',
          border: 'none',
        }}
      >
        <InvoicesUpload
          urlInvoicesS3={modalUploadFile?.urlInvoiceS3}
          urlAdditionalInvoicesS3={modalUploadFile?.urlAdditionalInvoicesS3}
          id={modalUploadFile?.id && modalUploadFile?.id}
          uploadInvoice={async (id: string, files: FileActivityObject) => {
            await uploadInvoice(id, files);
            setModalUploadFile({
              show: false,
              id: null,
              urlInvoiceS3: undefined,
              urlAdditionalInvoicesS3: undefined,
            });
          }}
        />
      </Modal>
      <TitlePart textTitle="Gestion des CRA" />
      <BackLinkContainer>
        <HrefLink href={isAdmin ? `/missions/${param.missionRecordId}` : '/missions'} target="_self">
          <BackLink>
            <ArrowBackIcon sx={{ marginRight: '8px', fontSize: '20px' }} />
            Retour aux missions
          </BackLink>
        </HrefLink>
      </BackLinkContainer>
      <CenteredTextContainer>
        <SingleLineText
          text={`Concernant ta mission ${mission.label} chez ${mission?.clientSocietyName?.[0]}`}
          variant="h6"
          sx={{
            margin: '12px 0',
            color: 'primary.main',
            fontWeight: 600,
          }}
        />
        <SingleLineText text="Pour renseigner ton compte rendu d'activité, clique sur le bouton 'Ajouter un CRA', puis sélectionne le mois concerné et déclare les jours où tu as travaillé." />
        <SingleLineText text="Une fois le CRA validé, tu recevras un mail pour uploader ta facture. Sans cette dernière étape, nous ne pouvons pas procéder au paiement de ta prestation. On compte sur toi ! :)" />
      </CenteredTextContainer>

      <StyledTable>
        <BasicTable columns={['Mois', 'Année', 'Total jours travaillés', 'Statut', 'Action']}>
          {arrayOfCRA?.map((el, i) => {
            const isZeroCRA = isZeroCRAActivity(el);
            const isSignedZeroCRA = isZeroCRA && !!el.validated;

            return (
              <TableRow key={i}>
                <TableCellBody>{el.monthString}</TableCellBody>
                <TableCellBody>{el.year}</TableCellBody>
                <TableCellBody>
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'center'}>
                    {el.nbTotalDaysWorked} <InformationTip>{getExplanation(el)}</InformationTip>
                  </Stack>
                </TableCellBody>
                <TableCellBody>
                  <TextStatusTypography variant="body1Light">{displayStatut(el)}</TextStatusTypography>
                </TableCellBody>
                <TableCell>
                  <ContainerSendSign>
                    {!isSignedZeroCRA && (
                      <ActionBundle
                        getNPS={getNPS}
                        stateNPS={stateNPS}
                        upsertFollowUp={upsertFollowUp}
                        mission={mission}
                        refetchActivities={refetchActivities}
                        sendFunc={async (signerFullname: string) => {
                          if (!el.contactOperations) {
                            notify(
                              'warning',
                              "Il semblerait qu'il manque des infos sur ton contact opérationnel, rapproche toi de ton Customer Care Manager !",
                            );
                          } else {
                            if (el.recordId) {
                              await signBloomerActivity(el.recordId, signerFullname);
                            } else {
                              notify('warning', 'Impossible de signer le CRA');
                            }
                          }
                        }}
                        activity={el}
                        uploadCRA={uploadCRA}
                        i={i}
                        CRAEditing={!el.validated && !el.signing && el.urlCraS3 === null}
                        CRACanSendInvite={el.nbTotalDaysWorked !== null || el.nbTotalDaysWorked !== undefined}
                        CRAUploaded={el.urlCraS3 === null && el.nbTotalDaysWorked !== 0}
                        CRASignSent={el.signing}
                        CRAValidated={el.validated}
                        canSeeInvoices={isAdmin || !bloomerUsePortage}
                        canUploadInvoices={
                          el.urlInvoicesS3 === null && el?.payed !== true && (isAdmin || !bloomerUsePortage)
                        }
                        editFunc={() => editionPage(el.date)}
                        cancelFunc={async () => {
                          await cancelBloomerActivity(el.recordId, i);
                        }}
                        setModalUploadBill={() => {
                          setModalUploadFile({
                            show: true,
                            id: el.recordId,
                            urlInvoiceS3: el.urlInvoicesS3,
                            urlAdditionalInvoicesS3: el.urlAdditionalInvoicesS3,
                          });
                        }}
                        canDeleteInvoices={isAdmin || !bloomerUsePortage}
                        deleteInvoice={deleteInvoice}
                      />
                    )}
                  </ContainerSendSign>
                </TableCell>
              </TableRow>
            );
          })}
        </BasicTable>
        {displayNewActivity && (
          <ContainerDisplayNewCRA>
            <DatePicker
              label="CRA à déclarer"
              value={newActivityValue as Date}
              handleChange={setNewActivityValue}
              openTo="month"
              views={['year', 'month']}
            />
            <Button
              sx={{ margin: '1em auto 0' }}
              textButton="Continuer"
              onClick={() => {
                if (newActivityValue) {
                  const newDate = new Date(newActivityValue);
                  // Extraire le mois et l'année sélectionnés (peu importe le jour dans newActivityValue)
                  const missionBegin = startOfMonth(new Date(mission.beginMission));
                  const missionEnd = endOfMonth(new Date(mission.endMission));

                  // Vérifier si la date est en dehors de la période de la mission
                  if (isBefore(missionBegin, newDate) || isAfter(missionEnd, newDate)) {
                    notify('warning', 'Impossible de créer des CRAs en dehors de la période de la mission');
                    return;
                  }

                  createRow(newDate);
                }
              }}
            />
          </ContainerDisplayNewCRA>
        )}
        {!displayNewActivity && (
          <Button
            id="addCra"
            disable={!!error}
            textButton="Ajouter un CRA"
            onClick={() => displayRow()}
            emoji={<i className="fas fa-plus"></i>}
          />
        )}
      </StyledTable>
    </div>
  );
};

export default TableManagementCRA;
export { TableManagementCRA };

const TableCellBody = styled(TableCell)(() => ({
  textAlign: 'center !important' as CanvasTextAlign,
}));

const ContainerDisplayNewCRA = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  backgroundColor: theme.palette.neutral.white,
  boxShadow: '2px 2px 20px lightgrey',
  borderRadius: 10,
  width: '40%',
  margin: '1em auto',
  padding: '1em',
  '@media screen and (max-width: 760px)': {
    width: '75%',
  },
}));

const StyledTable = styled('div')(({ theme }) => ({
  width: '90%',
  margin: '1em auto',
}));

const ContainerSendSign = styled('div')(() => ({
  margin: 'auto',
  display: 'flex',
  msFlexDirection: 'column',
  flexFlow: 'wrap',
  justifyContent: 'center',
  '@media screen and (max-width: 620px)': {
    width: 'auto',
  },
}));

const TextStatusTypography = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.neutral[10],
  display: 'inline-block',
  margin: 'auto',
  color: 'gray',
  padding: '0 0.5em',
}));

const CenteredTextContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  marginTop: '20px',
  marginBottom: '20px',
}));

const BackLinkContainer = styled('div')(() => ({
  width: '90%',
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: '16px',
  marginBottom: '8px',
  margin: '16px auto 8px auto',
}));

const BackLink = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.main,
  fontSize: '14px',
  fontWeight: 500,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.primary.dark,
  },
}));
